import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import useGoBack from '../hooks/useGoBack'
import {
  Card,
  Button,
  Table,
  Input,
  Select,
  DatePicker,
  message,
  Spin,
  Modal,
  Space,
  Breadcrumb,
  Typography,
  Pagination,
  Form,
  InputNumber,
  Row,
  Col,
  Popconfirm,
  notification
} from 'antd'
import {
  ArrowLeftOutlined,
  SearchOutlined,
  ReloadOutlined,
  FileTextOutlined,
  EyeOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import { useMediaQuery } from 'react-responsive'
import {
  getInventoryPurchaseRecords,
  getInventoryProducts,
  updateInventoryPurchaseRecord,
  deleteInventoryPurchaseRecord
} from '../api/inventory'
import api from '../utils/api'

const { RangePicker } = DatePicker
const { Text } = Typography

const colors = {
  primary: '#2C3E50',
  secondary: '#5D7A9B',
  text: { primary: '#1A1A1A', secondary: '#6B7280', tertiary: '#9CA3AF' },
  background: { primary: '#FFFFFF', secondary: '#FAFBFC', tertiary: '#F5F7FA' },
  border: '#E5E7EB'
}

const spacing = { xs: '8px', sm: '16px', md: '24px', lg: '32px' }

const PageContainer = styled.div`
  margin: 0 auto;
  padding: ${spacing.lg} ${spacing.md};
  background: ${colors.background.secondary};
  min-height: 100vh;
  @media (max-width: 820px) {
    padding: ${spacing.md} ${spacing.sm};
  }
`

const HeaderSection = styled.div`
  margin-bottom: ${spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing.sm};
`

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 300;
  color: ${colors.text.primary};
  margin: 0;
  letter-spacing: -0.5px;
  @media (max-width: 820px) {
    font-size: 24px;
  }
`

const FilterCard = styled(Card)`
  margin-bottom: ${spacing.md};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  background: ${colors.background.primary};
  border: 1px solid ${colors.border};
  .ant-card-body {
    padding: ${spacing.md};
    @media (max-width: 768px) {
      padding: ${spacing.sm};
    }
  }
`

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: flex-end;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const FilterItem = styled.div`
  flex: 1;
  min-width: 160px;
  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
  }
`

const ContentCard = styled(Card)`
  background: ${colors.background.primary};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: ${spacing.lg};
  .ant-card-body {
    padding: ${spacing.sm};
    overflow-x: auto;
    @media (max-width: 768px) {
      padding: ${spacing.xs};
    }
  }
`

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background: ${colors.background.tertiary};
    color: ${colors.text.primary};
    font-weight: 500;
    border-bottom: 1px solid ${colors.border};
  }
  .ant-table-tbody > tr > td {
    border-bottom: 1px solid ${colors.border};
  }
  .ant-table-tbody > tr:hover > td {
    background: ${colors.background.tertiary};
  }
  @media (max-width: 768px) {
    font-size: 13px;
  }
`

const SummaryCard = styled(Card)`
  margin-bottom: ${spacing.md};
  border-radius: 12px;
  border: 1px solid ${colors.border};
  background: ${colors.background.primary};
  .ant-card-body {
    padding: ${spacing.md};
  }
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${spacing.sm};
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const SummaryItem = styled.div`
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.background.tertiary};
  border-radius: 8px;
  text-align: center;
`

const SummaryLabel = styled.span`
  display: block;
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`

const SummaryValue = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.text.primary};
  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const MOBILE_BREAKPOINT = 768

const MobileCardList = styled.div`
  display: none;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: block;
  }
`

const TableWrapper = styled.div`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`

const RecordCard = styled(Card)`
  margin-bottom: ${spacing.sm};
  border-radius: 12px;
  border: 1px solid ${colors.border};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  .ant-card-body {
    padding: ${spacing.md};
  }
`

const RecordCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid ${colors.border};
  &:last-child {
    border-bottom: none;
  }
`

const RecordCardLabel = styled.span`
  font-size: 13px;
  color: ${colors.text.secondary};
`

const RecordCardValue = styled.span`
  font-size: 14px;
  color: ${colors.text.primary};
  font-weight: 500;
  text-align: right;
`

const MobilePaginationWrap = styled.div`
  display: none;
  padding: ${spacing.md} 0;
  justify-content: center;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
  }
`

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0'
  const n = Number(value) || 0
  return `$${n.toLocaleString()}`
}

const InventoryPurchaseRecordsPage = () => {
  const navigate = useNavigate()
  const goBack = useGoBack('/inventory/purchase')
  const { user } = useAuth()
  const { hasFeature } = useMembership()
  const { can } = usePermission()
  // 只有具備 inventory:manage（或 *）才能看金額相關欄位、編輯與刪除進貨紀錄
  const canManageInventory =
    can('inventory:manage') || can('inventory:*') || can('*')
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const [editForm] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [records, setRecords] = useState([])
  const [employees, setEmployees] = useState([])
  const [products, setProducts] = useState([])
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 })
  const [filters, setFilters] = useState({
    dateRange: null,
    employeeId: undefined,
    paymentMethod: undefined,
    search: ''
  })
  const [summary, setSummary] = useState(null)
  const [detailModalVisible, setDetailModalVisible] = useState(false)
  const [detailRecord, setDetailRecord] = useState(null)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [editingRecord, setEditingRecord] = useState(null)
  const [editItems, setEditItems] = useState([])
  const [submittingEdit, setSubmittingEdit] = useState(false)

  const fetchEmployees = useCallback(async () => {
    if (!user?.businessId) return
    try {
      const res = await api.get(`/api/employees/${user.businessId}`)
      const list = Array.isArray(res.data) ? res.data : res.data?.data || []
      setEmployees(list)
    } catch (e) {
      console.error('獲取員工列表失敗', e)
      setEmployees([])
    }
  }, [user?.businessId])

  const fetchProducts = useCallback(async () => {
    if (!user?.businessId || !hasFeature('inventory')) return
    try {
      const res = await getInventoryProducts({ page: 1, pageSize: 1000, status: 'active' })
      const list = res.data?.data?.items || res.data?.items || []
      setProducts(list)
    } catch (e) {
      console.error('獲取商品列表失敗', e)
      setProducts([])
    }
  }, [user?.businessId, hasFeature])

  const fetchRecords = useCallback(async () => {
    if (!user?.businessId || !hasFeature('inventory')) return
    setLoading(true)
    try {
      const params = {
        page: pagination.current,
        pageSize: pagination.pageSize
      }
      if (filters.dateRange?.[0]) params.startDate = filters.dateRange[0].format('YYYY-MM-DD')
      if (filters.dateRange?.[1]) params.endDate = filters.dateRange[1].format('YYYY-MM-DD')
      if (filters.employeeId) params.employeeId = filters.employeeId
      if (filters.paymentMethod) params.paymentMethod = filters.paymentMethod
      if (filters.search?.trim()) params.search = filters.search.trim()

      const res = await getInventoryPurchaseRecords(params)
      const data = res.data?.data || res.data || {}
      setRecords(data.records || data.items || [])
      setPagination((prev) => ({
        ...prev,
        total: data.pagination?.total ?? data.total ?? (data.records || data.items || []).length
      }))
      if (data.summary) setSummary(data.summary)
      else setSummary(null)
    } catch (e) {
      console.error('獲取進貨紀錄失敗', e)
      message.error(e.response?.data?.message || '獲取進貨紀錄失敗')
      setRecords([])
      setSummary(null)
    } finally {
      setLoading(false)
    }
  }, [user?.businessId, hasFeature, pagination.current, pagination.pageSize, filters])

  useEffect(() => {
    fetchEmployees()
    fetchProducts()
  }, [fetchEmployees, fetchProducts])

  useEffect(() => {
    if (hasFeature('inventory') && user?.businessId) fetchRecords()
  }, [fetchRecords, hasFeature, user?.businessId])

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }))
    fetchRecords()
  }

  const handleTableChange = (pag) => {
    setPagination((prev) => ({
      ...prev,
      current: pag.current,
      pageSize: pag.pageSize || prev.pageSize
    }))
  }

  const openDetail = (record) => {
    setDetailRecord(record)
    setDetailModalVisible(true)
  }

  const openEdit = (record) => {
    setEditingRecord(record)
    const items = Array.isArray(record.items) ? record.items : []
    setEditItems(
      items.map((it, i) => ({
        key: `edit_${i}_${Date.now()}`,
        productId: it.productId,
        productName: it.productName ?? '',
        quantity: it.quantity ?? 1,
        unitPrice: it.unitPrice ?? 0,
        totalPrice: (it.quantity ?? 1) * (it.unitPrice ?? 0)
      }))
    )
    editForm.setFieldsValue({
      employeeId: record.employeeId,
      paymentMethod: record.paymentMethod || 'cash',
      stockDate: record.stockDate ? dayjs(record.stockDate) : null,
      paymentDate: record.paymentDate ? dayjs(record.paymentDate) : null,
      note: record.note ?? ''
    })
    setEditModalVisible(true)
    setDetailModalVisible(false)
  }

  const closeEdit = () => {
    setEditModalVisible(false)
    setEditingRecord(null)
    setEditItems([])
    editForm.resetFields()
  }

  // 刪除進貨紀錄：呼叫 API 後 refetch；以 notification 顯示後端回傳的庫存／成本變化
  const handleDeletePurchase = async (record) => {
    if (!record?._id) return
    try {
      const res = await deleteInventoryPurchaseRecord(record._id)
      const data = res.data?.data || res.data || {}
      const restored = Array.isArray(data.restoredStocks)
        ? data.restoredStocks
        : []
      const synced = Array.isArray(data.syncedCosts) ? data.syncedCosts : []

      message.success('進貨記錄已刪除')

      // 用 notification 列出庫存與成本變化，方便店家確認
      if (restored.length > 0 || synced.length > 0) {
        notification.info({
          message: '已同步庫存與成本',
          duration: 6,
          description: (
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>
              {restored.length > 0 && (
                <div style={{ marginBottom: 6 }}>
                  <div style={{ fontWeight: 600 }}>庫存回滾：</div>
                  {restored.map((s) => (
                    <div key={s.productId}>
                      {s.productName}：{s.stockBefore} → {s.stockAfter}（-{s.quantity}）
                    </div>
                  ))}
                </div>
              )}
              {synced.length > 0 && (
                <div>
                  <div style={{ fontWeight: 600 }}>商品成本同步：</div>
                  {synced.map((c) => (
                    <div key={c.productId}>
                      {c.productName}：NT$ {c.costBefore} → NT$ {c.costAfter}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })
      }

      // 若刪掉的正是當前打開的明細，把 Modal 收掉
      if (detailRecord?._id === record._id) {
        setDetailModalVisible(false)
        setDetailRecord(null)
      }
      fetchRecords()
    } catch (e) {
      console.error('刪除進貨紀錄失敗:', e)
      message.error(e.response?.data?.message || '刪除失敗')
    }
  }

  const editAddItem = () => {
    setEditItems((prev) => [
      ...prev,
      {
        key: `edit_${Date.now()}`,
        productId: undefined,
        productName: '',
        quantity: 1,
        unitPrice: 0,
        totalPrice: 0
      }
    ])
  }

  const editRemoveItem = (key) => {
    setEditItems((prev) => prev.filter((it) => it.key !== key))
  }

  const editProductChange = (key, productId) => {
    const product = products.find((p) => p._id === productId)
    if (!product) return
    setEditItems((prev) =>
      prev.map((it) => {
        if (it.key !== key) return it
        const q = it.quantity || 1
        const up = product.cost ?? product.suggestedPrice ?? 0
        return {
          ...it,
          productId: product._id,
          productName: product.name || product.title,
          unitPrice: up,
          totalPrice: up * q
        }
      })
    )
  }

  const editQuantityChange = (key, quantity) => {
    const q = quantity || 1
    setEditItems((prev) =>
      prev.map((it) =>
        it.key === key ? { ...it, quantity: q, totalPrice: (it.unitPrice || 0) * q } : it
      )
    )
  }

  const editPriceChange = (key, unitPrice) => {
    const p = unitPrice || 0
    setEditItems((prev) =>
      prev.map((it) =>
        it.key === key ? { ...it, unitPrice: p, totalPrice: p * (it.quantity || 1) } : it
      )
    )
  }

  const editTotalAmount = editItems.reduce((sum, it) => sum + (it.totalPrice || 0), 0)

  const handleEditSubmit = async () => {
    if (!editingRecord?._id) return
    try {
      const values = await editForm.validateFields()
      if (editItems.length === 0) {
        message.warning('請至少保留一項商品')
        return
      }
      const hasEmpty = editItems.some((it) => !it.productId)
      if (hasEmpty) {
        message.warning('請為所有品項選擇商品')
        return
      }
      setSubmittingEdit(true)
      const employee = employees.find((e) => e._id === values.employeeId)
      const payload = {
        employeeId: values.employeeId,
        employeeName: employee?.name ?? editingRecord.employeeName,
        paymentMethod: values.paymentMethod,
        stockDate: values.stockDate.format('YYYY-MM-DD'),
        paymentDate: values.paymentDate.format('YYYY-MM-DD'),
        items: editItems.map((it) => ({
          productId: it.productId,
          productName: it.productName,
          quantity: Number(it.quantity) || 1,
          unitPrice: Number(it.unitPrice) || 0,
          totalPrice: Number(it.totalPrice) || 0
        })),
        totalAmount: editTotalAmount,
        note: values.note ?? ''
      }
      await updateInventoryPurchaseRecord(editingRecord._id, payload)
      message.success('進貨記錄已更新')
      closeEdit()
      fetchRecords()
    } catch (e) {
      if (e.errorFields) return
      console.error('更新進貨紀錄失敗', e)
      message.error(e.response?.data?.message || '更新進貨紀錄失敗')
    } finally {
      setSubmittingEdit(false)
    }
  }

  const columns = [
    {
      title: '進貨日期',
      dataIndex: 'stockDate',
      key: 'stockDate',
      width: 120,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-')
    },
    {
      title: '付款日期',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 120,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-')
    },
    {
      title: '經手人',
      dataIndex: 'employeeName',
      key: 'employeeName',
      width: 100,
      ellipsis: true
    },
    {
      title: '付款方式',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 90,
      render: (v) => (v === 'cash' ? '現金' : v === 'nonCash' ? '非現金' : v || '-')
    },
    {
      title: '品項數',
      key: 'itemCount',
      width: 80,
      align: 'center',
      render: (_, r) => (Array.isArray(r.items) ? r.items.length : 0)
    },
    // 沒有 inventory:manage 權限時隱藏金額欄
    ...(canManageInventory
      ? [
          {
            title: '總金額',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            width: 110,
            align: 'right',
            render: (v) => <Text strong>{formatCurrency(v)}</Text>
          }
        ]
      : []),
    {
      title: '備註',
      dataIndex: 'note',
      key: 'note',
      ellipsis: true,
      render: (v) => (v ? <span title={v}>{v}</span> : '-')
    },
    {
      title: '操作',
      key: 'action',
      width: canManageInventory ? 200 : 80,
      fixed: 'right',
      render: (_, record) => (
        <Space size='small'>
          <Button
            type='link'
            size='small'
            icon={<EyeOutlined />}
            onClick={() => openDetail(record)}
          >
            明細
          </Button>
          {canManageInventory && (
            <Button
              type='link'
              size='small'
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
            >
              編輯
            </Button>
          )}
          {canManageInventory && (
            <Popconfirm
              title='確定刪除此進貨紀錄？'
              description={
                <div style={{ maxWidth: 240 }}>
                  會同時回滾此筆進貨增加的庫存，
                  並依剩下最新一筆進貨單重新同步商品成本。此動作無法復原。
                </div>
              }
              okText='確定刪除'
              okButtonProps={{ danger: true }}
              cancelText='取消'
              onConfirm={() => handleDeletePurchase(record)}
            >
              <Button type='link' size='small' danger icon={<DeleteOutlined />}>
                刪除
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ]

  if (!hasFeature('inventory')) {
    return (
      <PageContainer>
        <Card>
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <Text type='secondary'>此功能需要庫存管理權限才能使用</Text>
          </div>
        </Card>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <HeaderSection>
        <div>
          <Breadcrumb
            items={[
              { title: <a onClick={() => navigate('/inventory/products')}>庫存管理</a> },
              { title: <a onClick={() => navigate('/inventory/purchase')}>進貨管理</a> },
              { title: '進貨紀錄' }
            ]}
          />
          <PageTitle>
            <FileTextOutlined style={{ marginRight: 8 }} />
            進貨紀錄查詢
          </PageTitle>
        </div>
        <Space wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={goBack}>
            返回進貨
          </Button>
          <Button icon={<ReloadOutlined />} onClick={fetchRecords} loading={loading}>
            重新整理
          </Button>
        </Space>
      </HeaderSection>

      <FilterCard>
        <FilterRow>
          <FilterItem>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>
              日期區間
            </label>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => handleFilterChange('dateRange', dates)}
              format='YYYY-MM-DD'
              style={{ width: '100%' }}
              placeholder={['開始日期', '結束日期']}
            />
          </FilterItem>
          <FilterItem>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>
              經手人
            </label>
            <Select
              placeholder='全部'
              allowClear
              value={filters.employeeId}
              onChange={(v) => handleFilterChange('employeeId', v)}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp='label'
              options={employees.map((e) => ({ value: e._id, label: e.name }))}
            />
          </FilterItem>
          <FilterItem>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>
              付款方式
            </label>
            <Select
              placeholder='全部'
              allowClear
              value={filters.paymentMethod}
              onChange={(v) => handleFilterChange('paymentMethod', v)}
              style={{ width: '100%' }}
              options={[
                { value: 'cash', label: '現金' },
                { value: 'nonCash', label: '非現金' }
              ]}
            />
          </FilterItem>
          <FilterItem>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>
              搜尋
            </label>
            <Input
              placeholder='備註關鍵字'
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onPressEnter={handleSearch}
              allowClear
              prefix={<SearchOutlined />}
              style={{ width: '100%' }}
            />
          </FilterItem>
          <FilterItem>
            <Button type='primary' icon={<SearchOutlined />} onClick={handleSearch} loading={loading}>
              查詢
            </Button>
          </FilterItem>
        </FilterRow>
      </FilterCard>

      {summary && (
        <SummaryCard size='small' title='查詢匯總'>
          <SummaryGrid>
            <SummaryItem>
              <SummaryLabel>筆數</SummaryLabel>
              <SummaryValue>{summary.count ?? summary.totalCount ?? '-'}</SummaryValue>
            </SummaryItem>
            {canManageInventory && (
              <SummaryItem>
                <SummaryLabel>總進貨金額</SummaryLabel>
                <SummaryValue>{formatCurrency(summary.totalAmount)}</SummaryValue>
              </SummaryItem>
            )}
          </SummaryGrid>
        </SummaryCard>
      )}

      <ContentCard>
        <Spin spinning={loading}>
          <TableWrapper>
            <StyledTable
              columns={columns}
              dataSource={records}
              rowKey={(r) => r._id || r.id || Math.random()}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showTotal: (t) => `共 ${t} 筆`,
                pageSizeOptions: ['10', '20', '50']
              }}
              onChange={handleTableChange}
              scroll={{ x: 800 }}
              size='middle'
              locale={{ emptyText: '尚無進貨紀錄' }}
            />
          </TableWrapper>

          <MobileCardList>
            {records.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px 16px',
                  color: colors.text.tertiary,
                  fontSize: 14
                }}
              >
                尚無進貨紀錄
              </div>
            ) : (
              records.map((record) => (
                <RecordCard key={record._id || record.id || Math.random()}>
                  <RecordCardRow>
                    <RecordCardLabel>進貨日期</RecordCardLabel>
                    <RecordCardValue>
                      {record.stockDate ? dayjs(record.stockDate).format('YYYY-MM-DD') : '-'}
                    </RecordCardValue>
                  </RecordCardRow>
                  <RecordCardRow>
                    <RecordCardLabel>付款日期</RecordCardLabel>
                    <RecordCardValue>
                      {record.paymentDate ? dayjs(record.paymentDate).format('YYYY-MM-DD') : '-'}
                    </RecordCardValue>
                  </RecordCardRow>
                  <RecordCardRow>
                    <RecordCardLabel>經手人</RecordCardLabel>
                    <RecordCardValue>{record.employeeName ?? '-'}</RecordCardValue>
                  </RecordCardRow>
                  <RecordCardRow>
                    <RecordCardLabel>付款方式</RecordCardLabel>
                    <RecordCardValue>
                      {record.paymentMethod === 'cash'
                        ? '現金'
                        : record.paymentMethod === 'nonCash'
                          ? '非現金'
                          : '-'}
                    </RecordCardValue>
                  </RecordCardRow>
                  <RecordCardRow>
                    <RecordCardLabel>品項數</RecordCardLabel>
                    <RecordCardValue>
                      {Array.isArray(record.items) ? record.items.length : 0}
                    </RecordCardValue>
                  </RecordCardRow>
                  {canManageInventory && (
                    <RecordCardRow>
                      <RecordCardLabel>總金額</RecordCardLabel>
                      <RecordCardValue style={{ fontWeight: 600, color: colors.primary }}>
                        {formatCurrency(record.totalAmount)}
                      </RecordCardValue>
                    </RecordCardRow>
                  )}
                  {record.note && (
                    <RecordCardRow>
                      <RecordCardLabel>備註</RecordCardLabel>
                      <RecordCardValue
                        style={{
                          fontWeight: 400,
                          maxWidth: '60%',
                          wordBreak: 'break-word',
                          textAlign: 'right'
                        }}
                      >
                        {record.note}
                      </RecordCardValue>
                    </RecordCardRow>
                  )}
                  <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                    <Button
                      type='primary'
                      block
                      size='small'
                      icon={<EyeOutlined />}
                      onClick={() => openDetail(record)}
                    >
                      明細
                    </Button>
                    {canManageInventory && (
                      <Button
                        block
                        size='small'
                        icon={<EditOutlined />}
                        onClick={() => openEdit(record)}
                      >
                        編輯
                      </Button>
                    )}
                    {canManageInventory && (
                      <Popconfirm
                        title='確定刪除此進貨紀錄？'
                        description='會回滾庫存並重新同步商品成本，此動作無法復原。'
                        okText='確定刪除'
                        okButtonProps={{ danger: true }}
                        cancelText='取消'
                        onConfirm={() => handleDeletePurchase(record)}
                      >
                        <Button
                          block
                          danger
                          size='small'
                          icon={<DeleteOutlined />}
                        >
                          刪除
                        </Button>
                      </Popconfirm>
                    )}
                  </div>
                </RecordCard>
              ))
            )}
          </MobileCardList>

          <MobilePaginationWrap>
            {records.length > 0 && pagination.total > 0 && (
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                onChange={(page, pageSize) =>
                  handleTableChange({
                    current: page,
                    pageSize: pageSize || pagination.pageSize
                  })
                }
                showSizeChanger={false}
                size='small'
                showTotal={(t) => `共 ${t} 筆`}
              />
            )}
          </MobilePaginationWrap>
        </Spin>
      </ContentCard>

      <Modal
        title='進貨明細'
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key='close' onClick={() => setDetailModalVisible(false)}>
            關閉
          </Button>,
          canManageInventory && (
            <Popconfirm
              key='delete'
              title='確定刪除此進貨紀錄？'
              description='會回滾庫存並重新同步商品成本，此動作無法復原。'
              okText='確定刪除'
              okButtonProps={{ danger: true }}
              cancelText='取消'
              onConfirm={() => detailRecord && handleDeletePurchase(detailRecord)}
            >
              <Button danger icon={<DeleteOutlined />}>
                刪除
              </Button>
            </Popconfirm>
          ),
          canManageInventory && (
            <Button key='edit' type='primary' icon={<EditOutlined />} onClick={() => detailRecord && openEdit(detailRecord)}>
              編輯
            </Button>
          )
        ].filter(Boolean)}
        width={Math.min(560, window.innerWidth - 32)}
        destroyOnClose
      >
        {detailRecord && (
          <div>
            <Space direction='vertical' style={{ width: '100%' }} size='middle'>
              <div>
                <Text type='secondary'>進貨日期：</Text>
                {detailRecord.stockDate ? dayjs(detailRecord.stockDate).format('YYYY-MM-DD') : '-'}
                {' · '}
                <Text type='secondary'>付款日期：</Text>
                {detailRecord.paymentDate ? dayjs(detailRecord.paymentDate).format('YYYY-MM-DD') : '-'}
              </div>
              <div>
                <Text type='secondary'>經手人：</Text>
                {detailRecord.employeeName ?? '-'}
                {' · '}
                <Text type='secondary'>付款方式：</Text>
                {detailRecord.paymentMethod === 'cash' ? '現金' : detailRecord.paymentMethod === 'nonCash' ? '非現金' : '-'}
              </div>
              {detailRecord.note && (
                <div>
                  <Text type='secondary'>備註：</Text>
                  {detailRecord.note}
                </div>
              )}
            </Space>
            {Array.isArray(detailRecord.items) && detailRecord.items.length > 0 && (
              isMobile ? (
                // 手機版：改為卡片清單，商品名稱完整顯示不被 ellipsis 截斷
                <div
                  style={{
                    marginTop: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8
                  }}
                >
                  {detailRecord.items.map((item, i) => (
                    <div
                      key={item.productId || i}
                      style={{
                        border: `1px solid ${colors.border}`,
                        borderRadius: 8,
                        padding: 10,
                        background: colors.background.primary
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          color: colors.text.primary,
                          wordBreak: 'break-word',
                          lineHeight: 1.4,
                          marginBottom: 6
                        }}
                      >
                        {item.productName || '-'}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: 13,
                          color: colors.text.secondary
                        }}
                      >
                        <span>數量：{item.quantity ?? '-'}</span>
                        {canManageInventory && (
                          <span>單價：{formatCurrency(item.unitPrice)}</span>
                        )}
                      </div>
                      {canManageInventory && (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: 6,
                            paddingTop: 6,
                            borderTop: `1px dashed ${colors.border}`
                          }}
                        >
                          <span style={{ color: colors.text.secondary, fontSize: 13 }}>
                            小計
                          </span>
                          <span style={{ fontWeight: 600 }}>
                            {formatCurrency(item.totalPrice)}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <Table
                  size='small'
                  dataSource={detailRecord.items}
                  columns={[
                    { title: '商品', dataIndex: 'productName', key: 'productName', ellipsis: true },
                    { title: '數量', dataIndex: 'quantity', key: 'quantity', width: 70, align: 'right' },
                    ...(canManageInventory
                      ? [
                          {
                            title: '單價',
                            dataIndex: 'unitPrice',
                            key: 'unitPrice',
                            width: 90,
                            align: 'right',
                            render: (v) => formatCurrency(v)
                          },
                          {
                            title: '小計',
                            dataIndex: 'totalPrice',
                            key: 'totalPrice',
                            width: 100,
                            align: 'right',
                            render: (v) => formatCurrency(v)
                          }
                        ]
                      : [])
                  ]}
                  rowKey={(r, i) => r.productId || i}
                  pagination={false}
                  style={{ marginTop: 16 }}
                />
              )
            )}
            {canManageInventory && (
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Text strong>總金額：{formatCurrency(detailRecord.totalAmount)}</Text>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title='編輯進貨紀錄'
        open={editModalVisible}
        onCancel={closeEdit}
        width={Math.min(720, window.innerWidth - 32)}
        destroyOnClose
        footer={[
          <Button key='cancel' onClick={closeEdit}>
            取消
          </Button>,
          <Button key='submit' type='primary' loading={submittingEdit} onClick={handleEditSubmit} icon={<EditOutlined />}>
            儲存
          </Button>
        ]}
      >
        {editingRecord && (
          <Form form={editForm} layout='vertical' style={{ marginTop: 16 }}>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name='employeeId'
                  label='經手人'
                  rules={[{ required: true, message: '請選擇經手人' }]}
                >
                  <Select placeholder='請選擇員工' showSearch optionFilterProp='label' allowClear>
                    {employees.map((e) => (
                      <Select.Option key={e._id} value={e._id} label={e.name}>
                        {e.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name='paymentMethod'
                  label='付款方式'
                  rules={[{ required: true, message: '請選擇付款方式' }]}
                >
                  <Select placeholder='請選擇'>
                    <Select.Option value='cash'>現金</Select.Option>
                    <Select.Option value='nonCash'>非現金</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name='stockDate'
                  label='進貨日期'
                  rules={[{ required: true, message: '請選擇進貨日期' }]}
                >
                  <DatePicker style={{ width: '100%' }} format='YYYY-MM-DD' placeholder='進貨日期' />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name='paymentDate'
                  label='付款日期'
                  rules={[{ required: true, message: '請選擇付款日期' }]}
                >
                  <DatePicker style={{ width: '100%' }} format='YYYY-MM-DD' placeholder='付款日期' />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name='note' label='備註'>
                  <Input.TextArea rows={2} placeholder='備註（選填）' />
                </Form.Item>
              </Col>
            </Row>
            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 500 }}>品項</span>
              <Button type='dashed' size='small' icon={<PlusOutlined />} onClick={editAddItem}>
                添加品項
              </Button>
            </div>
            {editItems.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: colors.text.tertiary, background: colors.background.tertiary, borderRadius: 8 }}>
                請點擊「添加品項」加入商品
              </div>
            ) : (
              <Table
                size='small'
                dataSource={editItems}
                rowKey='key'
                pagination={false}
                scroll={{ x: 600 }}
                columns={[
                  {
                    title: '商品',
                    key: 'product',
                    width: 200,
                    render: (_, row) => (
                      <Select
                        placeholder='選擇商品'
                        value={row.productId}
                        onChange={(v) => editProductChange(row.key, v)}
                        style={{ width: '100%' }}
                        showSearch
                        optionFilterProp='label'
                      >
                        {products.map((p) => (
                          <Select.Option
                            key={p._id}
                            value={p._id}
                            label={p.name || p.title}
                          >
                            {p.name || p.title}
                          </Select.Option>
                        ))}
                      </Select>
                    )
                  },
                  {
                    title: '數量',
                    key: 'quantity',
                    width: 100,
                    render: (_, row) => (
                      <InputNumber
                        min={1}
                        value={row.quantity}
                        onChange={(v) => editQuantityChange(row.key, v)}
                        style={{ width: '100%' }}
                      />
                    )
                  },
                  {
                    title: '單價',
                    key: 'unitPrice',
                    width: 110,
                    render: (_, row) => (
                      <InputNumber
                        min={0}
                        step={0.01}
                        precision={2}
                        value={row.unitPrice}
                        onChange={(v) => editPriceChange(row.key, v)}
                        style={{ width: '100%' }}
                        prefix='$'
                      />
                    )
                  },
                  {
                    title: '小計',
                    key: 'totalPrice',
                    width: 100,
                    render: (_, row) => formatCurrency(row.totalPrice)
                  },
                  {
                    title: '',
                    key: 'action',
                    width: 56,
                    render: (_, row) => (
                      <Button
                        type='link'
                        danger
                        size='small'
                        icon={<DeleteOutlined />}
                        onClick={() => editRemoveItem(row.key)}
                      />
                    )
                  }
                ]}
              />
            )}
            {editItems.length > 0 && (
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Text strong>總金額：{formatCurrency(editTotalAmount)}</Text>
              </div>
            )}
          </Form>
        )}
      </Modal>
    </PageContainer>
  )
}

export default InventoryPurchaseRecordsPage
